import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  LocalizationProvider,
  StaticDatePicker,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { connect } from "react-redux";
import {
  loadAvailableDates,
  loadAvailableHours,
  createAppointment,
} from "../../../_redux/actions/appointmentActions";
import MiniCalendar from "./MiniCalendar";
import { TimeLoader, BarLoader } from "@/_components";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment-timezone";
import { utilities, langConversionHelper } from "@/_helpers";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GreenButton } from "../../../_helpers/customMUI";
import TextField from "@mui/material/TextField";
import { enUS, enAU, es } from "date-fns/locale";
import { format } from "date-fns";
import Cookies from "universal-cookie";
import { useToaster } from "../../toaster/MuiToasterProvider";
import BeatLoader from "react-spinners/BeatLoader";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { GridLoader } from "react-spinners";

const AppointmentDateSelection = ({
  loadAvailableDates,
  availableDates,
  loadAvailableHours,
  availableHours,
  selectedLocation,
  handleUpdate,
  submitting,
  setOpen,
  setStepSchedule,
  STEPS,
  setSelectedId,
  setClickMe,
  deviceType,
  preferProvidedDeviceType = false,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showMonthly, setShowMonthly] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const [selectedMonth, setSelectedMonth] = useState();
  const [serviceType, setServiceType] = useState();
  const [selectedHour, setSelectedHour] = useState();
  const [selectedBay, setSelectedBay] = useState(0);
  const { t, i18n } = useTranslation();
  const isDesktop = useMediaQuery("(min-width:1046px)");
  const isMobile = useMediaQuery("(min-width:600px)");
  const cookies = new Cookies();
  const [locale, setLocale] = useState(countryCode === "USA" ? enUS : enAU);
  const siteCountry = cookies.get("siteCountry");
  const toast = useToaster();
  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser"));

  const selectedDeviceType = preferProvidedDeviceType
    ? deviceType
    : enrolleeUser?.deviceType;
  const normalizedDeviceType = String(selectedDeviceType || "").toLowerCase();
  const locationDeviceTypeData = ["pam", "portable"].includes(
    normalizedDeviceType,
  )
    ? "PAM"
    : ["iid", "interlock"].includes(normalizedDeviceType)
      ? "IID"
      : ["isa", "speed", "speedv"].includes(normalizedDeviceType)
        ? "ISA"
        : enrolleeUser?.clientTypeId === 5
          ? "PAM"
          : enrolleeUser?.clientTypeId === 6
            ? "ISA"
            : "IID";
  const deviceTypeData = preferProvidedDeviceType
    ? locationDeviceTypeData
    : enrolleeUser?.deviceType === "Interlock" ||
        enrolleeUser?.clientTypeId === 1
      ? "IID"
      : enrolleeUser?.deviceType === "Portable" ||
          enrolleeUser?.clientTypeId === 5
        ? "PAM"
        : enrolleeUser?.deviceType === "Speed" ||
            enrolleeUser?.clientTypeId === 6
          ? "ISA"
          : "IID";

  useEffect(() => {
    setServiceType("Installation");
    setSelectedMonth(new Date());
  }, []);

  function formatDate(date) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  function formatDateForApi(date) {
  return moment(date).format("YYYY-MM-DD");
}

  useEffect(() => {
    if (
      selectedMonth !== undefined &&
      serviceType !== undefined &&
      enrolleeUser?.clientId &&
      selectedLocation?.locationID
    ) {
      setLoadingDates(true);
      const month = selectedMonth.getMonth() + 1;
      const day = selectedMonth.getDate();
      const formattedAptDate = `${selectedMonth.getFullYear()}-${
        month < 10 ? `0${month}` : month
      }-${day < 10 ? `0${day}` : day}`;
      const vehicleId =
        enrolleeUser.vehicleId === 0 ||
        enrolleeUser.vehicleId === null ||
        enrolleeUser.vehicleId === undefined
          ? ""
          : enrolleeUser.vehicleId;
      loadAvailableDates(
        enrolleeUser.clientId,
        vehicleId,
        selectedLocation?.locationID,
        deviceTypeData,
        serviceType,
        formattedAptDate,
        selectedLocation?.sameDay || false,
      )
        .catch((error) => console.log("Loading dates failed! " + error))
        .finally(() => setLoadingDates(false));
    }
  }, [
    selectedMonth,
    serviceType,
    enrolleeUser?.clientId,
    selectedLocation?.locationID,
    deviceTypeData,
    loadAvailableDates,
  ]);

  useEffect(() => {
    if (selectedDate) {
      let formattedDate = formatDate(selectedDate);
      setShowAllTimes(false);
      if (enrolleeUser?.clientId && selectedLocation?.locationID) {
        loadAvailableHours(
          enrolleeUser.clientId,
          selectedLocation?.locationID,
          deviceTypeData,
          "Installation",
          formattedDate,
        ).catch((error) => {
          console.log("Loading available hours failed! " + error);
        });
      }
    }
  }, [
    selectedDate,
    enrolleeUser?.clientId,
    selectedLocation?.locationID,
    deviceTypeData,
    loadAvailableHours,
  ]);

  const [showAllTimes, setShowAllTimes] = useState(false);
  const uniqueAvailableHours = useMemo(() => {
    if (!availableHours?.length) return [];
    const seen = new Set();
    return availableHours.filter((slot) => {
      if (!slot?.hour || seen.has(slot.hour)) return false;
      seen.add(slot.hour);
      return true;
    });
  }, [availableHours]);

  const visibleHours = showAllTimes
    ? uniqueAvailableHours
    : uniqueAvailableHours.slice(0, 2);

  useEffect(() => {
    if (i18n.language.startsWith("en")) {
      if (siteCountry === "Australia") {
        setLocale(enAU);
      } else {
        setLocale(enUS);
      }
    } else {
      setLocale(es);
    }
  }, [i18n.language]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedHour(null);
  };

  const handleClick = (h, b) => {
    setSelectedHour(h);
    setSelectedBay(b);
  };

  const handleSaveAppointment = async () => {
    // let dateString =
    //   moment(new Date(selectedDate)).format("YYYY-MM-DD") +
    //   " " +
    //   utilities.convertTo24H(selectedHour?.hour);
    // let aptDateTimeUTC = moment
    //   .tz(dateString, "America/New_York")
    //   .toISOString();
     const appointmentDateTime = `${formatDateForApi(
      selectedDate,
    )} ${utilities.convertTo24H(selectedHour.hour)}`;
    let apptDTFormatted =
      utilities.getMonthName(selectedDate) + " " + selectedDate.getDate();

    let isVehicleClient = {};

    if (enrolleeUser?.deviceType !== "PAM") {
      isVehicleClient = {
        vehYear: null,
        make: null,
        model: null,
      };
    }

    let appointmentData = {
      appointmentID: 0,
      vehicleID: isVehicleClient ? enrolleeUser?.vehicleId : 0,
      clientID: enrolleeUser?.clientId,
      beginDT: appointmentDateTime,
      appointmentDate: apptDTFormatted,
      appointmentTime: selectedHour?.hour,
      serviceID:
        enrolleeUser?.deviceType === "Interlock" ||
        enrolleeUser?.clientTypeId === 1
          ? 1
          : enrolleeUser?.deviceType === "Portable" ||
              enrolleeUser?.clientTypeId === 5
            ? 290
            : enrolleeUser?.deviceType === "Speed" ||
                enrolleeUser?.clientTypeId === 6
              ? 396
              : 0,
      appointmentStatusID: 1,
      appointmentType: "Installation",
      locationID: selectedLocation.locationID,
      bayID: selectedBay,
      locationName: selectedLocation.displayName,
      address1: selectedLocation.address1,
      address2: selectedLocation.address2,
      city: selectedLocation.city,
      state: selectedLocation.state,
      zip: selectedLocation.zip,
      deviceType: deviceTypeData,
      vehYear: isVehicleClient ? enrolleeUser?.vehYear : 0,
      make: isVehicleClient ? enrolleeUser?.make : "",
      model: isVehicleClient ? enrolleeUser?.model : "",
      isFranchise: false,
    };
    handleUpdate("Schedule", appointmentData);
    try {
      sessionStorage.setItem("appointmentCreatedFlag", true);
    } catch (error) {
      console.error("Error getting enrollment:", error);
      let errMess = error.message;
      errMess = errMess?.split(":").pop().replace('"', "").slice(0, -2);
      toast.error(errMess);
    }
  };

  const firstAvailableDate = (() => {
    if (
      !availableDates?.length ||
      availableDates[0]?.CurrentState === "Undefined" ||
      availableDates[0]?.CurrentState === "Undefined"
    )
      return null;

    const rawDate = availableDates[0]?.date;
    if (!rawDate) return null;

    const [month, day, year] = rawDate.split(" ")[0].split("/");
    const date = new Date(year, month - 1, day);

    return isNaN(date) ? null : date;
  })();

  const dateMonth = firstAvailableDate
    ? firstAvailableDate.toLocaleString("default", { month: "long" })
    : "";

  function IsDayNotAvailable(day) {
    var date = new Date(day);
    const formattedDate =
      (date.getMonth() > 8
        ? date.getMonth() + 1
        : "0" + (date.getMonth() + 1)) +
      "/" +
      (date.getDate() > 9 ? date.getDate() : "0" + date.getDate()) +
      "/" +
      date.getFullYear();
    if (
      !availableDates?.length ||
      availableDates[0].CurrentState === "Undefined" ||
      availableDates[0].CurrentState === "Undefined"
    ) {
      return false;
    } else {
      var found = { val: false };
      for (var i = 0; i < availableDates.length; i++) {
        if (
          availableDates[i].date &&
          availableDates[i].date != undefined &&
          availableDates[i].date.substr(0, 10) == formattedDate
        ) {
          found.val = true;
          break;
        }
      }
      return !found.val;
    }
  }

  const handleSetOpenModal = (location) => {
    setOpen(true);
    setSelectedId(location?.locationID);
    setStepSchedule(STEPS.SCHEDULE_NOW);
    setClickMe(true);
  };

  const handleShowAllTime = () => {
    setShowAllTimes((prev) => !prev);
  };

  useEffect(() => {
    const state = availableDates?.[0]?.CurrentState;

    if (!availableDates?.length) return;
    if (state === "Undefined" || state === "None") return;

    const today = new Date();
    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(today.getDate() + 14);

    const hasNearDates = availableDates?.some((d) => {
      const [m, d2, y] = d.date.split(" ")[0].split("/");
      const date = new Date(y, m - 1, d2);
      return date >= today && date <= twoWeeksFromNow;
    });

    if (!hasNearDates) {
      setShowMonthly(true);
    }
  }, [availableDates]);

  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, selected, disabled, ...other } = props;

    const isAvailable = !IsDayNotAvailable(day);

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={selected}
        disabled={disabled || !isAvailable}
        sx={{
          width: "44px !important",
          height: "44px !important",
          minWidth: "44px !important",
          minHeight: "44px !important",
          borderRadius: 1,
          backgroundColor: isAvailable
            ? "#f2f2f2 !important"
            : "transparent !important",
          cursor: isAvailable ? "pointer" : "not-allowed",
          color: isAvailable ? "inherit" : "#ccc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          border: "1px solid transparent !important",
          outline: "none !important",
          boxShadow: selected ? "0 0px 5px #76bc21" : "none",

          "&:hover": {
            backgroundColor: isAvailable
              ? "#f2f2f2 !important"
              : "transparent !important",
            boxShadow: selected ? "0 0px 5px #76bc21" : "none",
            color: "#000",
          },

          "&.Mui-selected": {
            backgroundColor: isAvailable
              ? "#f2f2f2 !important"
              : "transparent !important",
            border: "2px solid #76bc21 !important",
            boxShadow: "0 0px 5px #76bc21",
            color: "#000",
          },

          "&.Mui-selected:hover": {
            color: "#000",
          },

          "&.Mui-focusVisible": {
            outline: "none !important",
          },

          "&.MuiPickersDay-root": {
            width: "44px !important",
            height: "44px !important",
          },

          "&.MuiPickersDay-today::after": {
            content: '""',
            width: 5,
            height: 5,
            backgroundColor: "#1c355e",
            borderRadius: "50%",
            position: "absolute",
            bottom: 4,
            left: "50%",
            transform: "translateX(-50%)",
            pointerEvents: "none",
          },
        }}
      />
    );
  };

  CustomDay.propTypes = {
    day: PropTypes.instanceOf(Date).isRequired,
    outsideCurrentMonth: PropTypes.bool,
    selected: PropTypes.bool,
    disabled: PropTypes.bool,
  };

  const getTimePeriod = (hour) => {
    const h =
      hour?.includes("AM") || hour?.includes("PM")
        ? new Date(`1970-01-01 ${hour}`).getHours()
        : parseInt(hour?.split(":")[0], 10);

    if (h < 12) return "morning";
    if (h < 17) return "afternoon";
    return "evening";
  };

  const groupedHours = {
    morning: [],
    afternoon: [],
    evening: [],
  };

  uniqueAvailableHours?.forEach((time) => {
    const period = getTimePeriod(time?.hour);
    groupedHours[period].push(time);
  });

  useEffect(() => {
    if (!availableDates?.length) return;

    const state = availableDates[0]?.CurrentState;
    if (state === "Undefined" || state === "None") return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());

    const twoWeekEnd = new Date(weekStart);
    twoWeekEnd.setDate(weekStart.getDate() + 13);

    const hasDatesInRange = availableDates.some((d) => {
      if (!d?.date) return false;
      const [m, day, y] = d.date.split(" ")[0].split("/");
      const date = new Date(y, m - 1, day);
      date.setHours(0, 0, 0, 0);
      return date >= weekStart && date <= twoWeekEnd;
    });

    setShowMonthly(!hasDatesInRange);
  }, [availableDates]);

  const renderSection = (title, times) => {
    if (!times.length) return null;

    return (
      <Box mb={2}>
        <Typography mb={1} sx={{ fontWeight: 600 }}>
          {title}
        </Typography>

        <Box
          display="grid"
          sx={{ gridTemplateColumns: "repeat(3, 1fr)" }}
          gap={isMobile ? 0.4 : 1}
        >
          {times.map((time) => (
            <Box
              key={time.hour}
              onClick={() => handleClick(time, time.bayID)}
              sx={{
                paddingX: "10px",
                paddingY: "8px",
                height: "40px !important",
                borderRadius: 1,
                cursor: "pointer",
                textAlign: "center",
                maxWidth: "250px",
                bgcolor: selectedHour === time ? "#cfe8fd" : "#fff",
                "&:hover": {
                  bgcolor: "#cfe8fd",
                  boxShadow:
                    selectedHour === time
                      ? "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px"
                      : "none",
                },
                border: "0.5px solid #ddd",
                boxShadow:
                  selectedHour === time
                    ? "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px"
                    : "none",
              }}
            >
              {time.hour}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const datesState = availableDates?.[0]?.CurrentState;
  const showDatesUI = datesState !== "Undefined" && datesState !== "None";
  const showTopBlock = !!selectedLocation;
  const showPageLoader = !showTopBlock && !showDatesUI;

  const formatWeekDayLabel = (day) => {
    try {
      const date = typeof day === "string" ? new Date(day) : day;
      return format(date, "EEE", { locale }).toUpperCase();
    } catch (error) {
      return "";
    }
  };

  return (
    <Box className="customCalendar schedule-appointment-story">
      <>
        {showPageLoader ? (
          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BarLoader />
          </Grid>
        ) : (
          <>
            {showTopBlock && (
              <Grid>
                <Grid
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Grid sx={{ width: "100%" }}>
                    <Typography
                      sx={{
                        fontSize: 24,
                        fontWeight: "bold",
                        textAlign: "center",
                        mb: 2,
                        color: "#4b9CD3",
                      }}
                    >
                      {t("location.scheduleYourAppointment")}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: "bold",
                        mb: 1,
                        ml: isDesktop ? 2 : 0,
                      }}
                    >
                      {t("location.lifesaferShop")}
                    </Typography>

                    {/* <Typography
                      sx={{ fontSize: 16, mb: 2, ml: isDesktop ? 2 : 0 }}
                    >
                      {selectedLocation?.displayName} <br />
                      {selectedLocation?.address1}{" "}
                      {selectedLocation?.address2 || ""}
                      <br />
                      {selectedLocation?.city}, {selectedLocation?.state}{" "}
                      {selectedLocation?.zip}
                    </Typography> */}
                    <Typography
                      sx={{ fontSize: 16, mb: 2, ml: isDesktop ? 2 : 0 }}
                    >
                      {selectedLocation?.displayName && (
                        <>
                          {selectedLocation.displayName} <br />
                        </>
                      )}

                      {(selectedLocation?.address1 ||
                        selectedLocation?.address2) && (
                        <>
                          {selectedLocation?.address1}
                          {selectedLocation?.address2
                            ? ` ${selectedLocation.address2}`
                            : ""}
                          <br />
                        </>
                      )}

                      {(selectedLocation?.city ||
                        selectedLocation?.state ||
                        selectedLocation?.zip) && (
                        <>
                          {selectedLocation?.city}
                          {selectedLocation?.city && selectedLocation?.state
                            ? ", "
                            : ""}
                          {selectedLocation?.state}
                          {(selectedLocation?.city ||
                            selectedLocation?.state) &&
                          selectedLocation?.zip
                            ? " "
                            : ""}
                          {selectedLocation?.zip}
                        </>
                      )}
                    </Typography>

                    <Button
                      variant="text"
                      onClick={() => handleSetOpenModal(selectedLocation)}
                      style={{
                        color: "#4b9CD3",
                        marginLeft: "16px",
                        fontWeight: "bold",
                        marginBottom: "16px",
                      }}
                    >
                      <u>{t("location.moreLocations")}</u>
                    </Button>
                    {!isDesktop && (
                      <Grid>
                        <Typography
                          sx={{ fontSize: 18, fontWeight: "bold", mb: 1 }}
                        >
                          {t("location.scheduleLike")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 16,
                            mb: 2,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {t("location.installationTime")}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {showDatesUI ? (
              <>
                {isDesktop ? (
                  <>
                    <Paper elevation={1} sx={{ padding: 2, maxWidth: "100%" }}>
                      <Grid sx={{ overflowWrap: "anywhere" }}>
                        <Typography
                          sx={{ fontSize: 18, fontWeight: "bold", mb: 1 }}
                        >
                          {t("location.scheduleLike")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 16,
                            mb: 2,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            maxWidth: "340px",
                          }}
                        >
                          {t("location.installationTime")}
                        </Typography>
                      </Grid>
                      <Grid
                        sx={{
                          display: "flex",
                          alignItems: "start",
                          justifyContent: isMobile ? "flex-start" : "center",
                          gap: showMonthly ? "24px" : "24px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Grid sx={{ paddingTop: "0px" }}>
                          {!showMonthly && (
                            <Typography
                              sx={{
                                fontSize: 18,
                                fontWeight: "bold",
                                mb: 1,
                              }}
                            >
                              {langConversionHelper.monthConversion(
                                dateMonth,
                                i18n.language,
                              )}
                            </Typography>
                          )}

                          {showMonthly ? (
                            <LocalizationProvider
                              dateAdapter={AdapterDateFns}
                              adapterLocale={locale}
                            >
                              <StaticDatePicker
                                displayStaticWrapperAs="desktop"
                                sx={{
                                  "& .MuiPickersLayout-contentWrapper": {
                                    alignItems: "stretch",
                                  },
                                  "& .MuiDateCalendar-root": {
                                    width: 335,
                                    maxWidth: "none",
                                    maxHeight: "380px !important",
                                    height: "380px !important",
                                  },
                                  "& .MuiDayCalendar-weekDayLabel": {
                                    width: 44,
                                  },
                                  "& .MuiDayCalendar-weekContainer": {
                                    justifyContent: "flex-start",
                                  },
                                }}
                                disablePast
                                dayOfWeekFormatter={formatWeekDayLabel}
                                slotProps={{
                                  actionBar: { actions: [] },
                                  dayCalendar: {
                                    weekDayLabel: {
                                      sx: {
                                        width: 44,
                                        textAlign: "center",
                                        fontWeight: 600,
                                      },
                                    },
                                  },
                                }}
                                slots={{ day: CustomDay }}
                                value={selectedDate}
                                onChange={handleDateChange}
                                shouldDisableDate={IsDayNotAvailable}
                                renderInput={(params) => (
                                  <TextField {...params} />
                                )}
                              />
                            </LocalizationProvider>
                          ) : (
                            <MiniCalendar
                              availableDates={availableDates}
                              selectedDate={selectedDate}
                              setSelectedDate={setSelectedDate}
                              setSelectedHour={setSelectedHour}
                              locale={locale}
                              onShowMonthly={() => setShowMonthly(true)}
                            />
                          )}
                          {!showMonthly &&
                            (availableDates[0]?.CurrentState !== "Undefined" ||
                              availableDates[0]?.CurrentState !== "None") && (
                              <Grid
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <Button
                                  variant="text"
                                  sx={{
                                    mt: 1,
                                    textDecoration: "underline",
                                    fontWeight: "bold",
                                    color: "#4b9CD3",
                                  }}
                                  onClick={() => setShowMonthly(true)}
                                >
                                  {t("location.moreDate")}
                                </Button>
                              </Grid>
                            )}
                        </Grid>

                        <Grid sx={{ paddingTop: "0px" }}>
                          {selectedDate && (
                            <>
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  mb: 2,
                                  fontSize: 18,
                                }}
                              >
                                {t("location.availableAppointment")}
                              </Typography>

                              {availableHours[0]?.CurrentState !==
                              "Undefined" ? (
                                <>
                                  {renderSection(
                                    t("location.morning"),
                                    groupedHours.morning,
                                  )}
                                  {renderSection(
                                    t("location.afternoon"),
                                    groupedHours.afternoon,
                                  )}
                                  {renderSection(
                                    t("location.evening"),
                                    groupedHours.evening,
                                  )}
                                </>
                              ) : (
                                <Grid
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <TimeLoader />
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </>
                ) : (
                  <>
                    {!showMonthly && (
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: "bold",
                          mb: 1,
                        }}
                      >
                        {langConversionHelper.monthConversion(
                          dateMonth,
                          i18n.language,
                        )}
                      </Typography>
                    )}

                    {showMonthly ? (
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locale}
                      >
                        <StaticDatePicker
                          displayStaticWrapperAs="desktop"
                          sx={{
                            "& .MuiPickersLayout-contentWrapper": {
                              alignItems: "stretch",
                            },
                            "& .MuiDateCalendar-root": {
                              width: 335,
                              maxWidth: "none",
                              maxHeight: "380px !important",
                              height: "380px !important",
                            },
                            "& .MuiDayCalendar-weekDayLabel": {
                              width: 44,
                            },
                          }}
                          disablePast
                          dayOfWeekFormatter={formatWeekDayLabel}
                          slotProps={{
                            actionBar: { actions: [] },
                            dayCalendar: {
                              weekDayLabel: {
                                sx: {
                                  width: 44,
                                  textAlign: "center",
                                  fontWeight: 600,
                                },
                              },
                            },
                          }}
                          slots={{ day: CustomDay }}
                          value={selectedDate}
                          onChange={handleDateChange}
                          shouldDisableDate={IsDayNotAvailable}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    ) : (
                      <MiniCalendar
                        availableDates={availableDates}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        setSelectedHour={setSelectedHour}
                        locale={locale}
                        onShowMonthly={() => setShowMonthly(true)}
                      />
                    )}

                    {!showMonthly && (
                      <Grid
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="text"
                          sx={{
                            mt: 1,
                            textDecoration: "underline",
                            fontWeight: "bold",
                            color: "#4b9CD3",
                          }}
                          onClick={() => setShowMonthly(true)}
                        >
                          {t("location.moreDate")}
                        </Button>
                      </Grid>
                    )}
                  </>
                )}
              </>
            ) : loadingDates ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mt: "24px",
                  }}
                >
                  <GridLoader />
                </Box>
              </>
            ) : (
              <>
                <Grid
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 3,
                    px: 2,
                  }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      textAlign: "left",
                      height: 250,
                      width: 320,
                      mx: "auto",
                    }}
                  >
                    <Grid container direction="column">
                      <Grid sx={{ mt: 0.5, textAlign: "center" }}>
                        <EventBusyIcon
                          sx={{
                            color: "#1c355e",
                            width: 200,
                            height: 200,
                            opacity: 0.1,
                          }}
                        />
                        <Typography
                          sx={{
                            textAlign: "center",
                            zIndex: 10,
                            fontSize: 20,
                            mt: -20,
                          }}
                        >
                          {t("location.noAvailableDates")}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </>
            )}
          </>
        )}
      </>

      {!isDesktop && selectedDate && showDatesUI && (
        <Box mt={2}>
          <Typography sx={{ fontWeight: "bold", mb: 1, fontSize: 18 }}>
            {t("location.availableAppointment")}
          </Typography>

          {availableHours[0]?.CurrentState !== "Undefined" ? (
            <Box display="grid" gap={1}>
              {visibleHours.map((time) => (
                <Box
                  key={time.hour}
                  onClick={() => handleClick(time, time.bayID)}
                  sx={{
                    padding: "10px",
                    borderRadius: 1,
                    height: "40px !important",
                    cursor: "pointer",
                    textAlign: "center",
                    bgcolor: selectedHour === time ? "#cfe8fd" : "#fff",
                    "&:hover": {
                      bgcolor: "#cfe8fd",
                    },
                    boxShadow:
                      "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
                  }}
                >
                  {time.hour}
                </Box>
              ))}
              {!showAllTimes && uniqueAvailableHours.length > 2 && (
                <Button
                  variant="text"
                  onClick={() => handleShowAllTime()}
                  style={{
                    color: "#4b9CD3",
                    fontWeight: "bold",
                    marginLeft: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <u>{t("location.moreTimes")}</u>
                </Button>
              )}
            </Box>
          ) : (
            <Grid
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <TimeLoader />
            </Grid>
          )}
        </Box>
      )}

      {availableHours[0]?.CurrentState !== "Undefined" && showDatesUI && (
        <>
          {submitting === false ? (
            <Grid
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <GreenButton
                fullWidth
                disabled={!selectedHour}
                onClick={handleSaveAppointment}
                sx={{
                  mt: 3,
                  height: "40px !important",
                  fontWeight: 600,
                  bgcolor: "#76bc21",
                  color: "#1c355e !important",
                  "&:disabled": { opacity: 0.6 },
                  maxWidth: isDesktop ? "250px" : "",
                  minWidth: isDesktop ? "400px" : "100%",
                }}
                endIcon={<ChevronRightIcon />}
              >
                {t("steps.scheduleNow")}
              </GreenButton>
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: "24px",
              }}
            >
              <Typography sx={{ fontWeight: "600", fontSize: "20px" }}>
                {t("steps.saving")}
              </Typography>
              <BeatLoader color="#76bc21" />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

function mapStateToProps(state) {
  return {
    availableDates: state.availableDates,
    availableHours: state.availableHours,
  };
}

const mapDispatchToProps = {
  loadAvailableDates,
  loadAvailableHours,
  createAppointment,
};

AppointmentDateSelection.propTypes = {
  setStep: PropTypes.func.isRequired,
  setScheduleOpen: PropTypes.func,
  enrolleeUser: PropTypes.object,
  loadAvailableDates: PropTypes.func,
  loadAvailableHours: PropTypes.func,
  availableDates: PropTypes.array,
  availableHours: PropTypes.array,
  createAppointment: PropTypes.func,
  selectedLocation: PropTypes.object,
  handleUpdate: PropTypes.func,
  submitting: PropTypes.bool,
  setOpen: PropTypes.func,
  STEPS: PropTypes.object,
  setStepSchedule: PropTypes.func,
  setSelectedId: PropTypes.func,
  setClickMe: PropTypes.func,
  deviceType: PropTypes.string,
  preferProvidedDeviceType: PropTypes.bool,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AppointmentDateSelection);
